/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  useCallback,
} from 'react';
import {
  DEFAULT_CURRENCY,
  SupportedCurrencies,
  SUPPORTED_CURRENCIES,
} from './currencies';

type CurrencyContextType = {
  currency: SupportedCurrencies;
  currencyInfo: { shortSymbol: string; longform: string };
  setCurrency: (currency: SupportedCurrencies) => void;
};

const CurrencyContext = createContext<CurrencyContextType | undefined>(
  undefined,
);

type CurrencyProviderProps = {
  children: ReactNode;
  initialCurrency?: SupportedCurrencies;
  onCurrencyChange?: (currency: SupportedCurrencies) => void;
};

export const CurrencyProvider: React.FC<CurrencyProviderProps> = ({
  children,
  initialCurrency = DEFAULT_CURRENCY,
  onCurrencyChange,
}) => {
  const [currency, setCurrencyState] =
    useState<SupportedCurrencies>(initialCurrency);

  // Sync with external currency changes (from AppConfig)
  useEffect(() => {
    if (initialCurrency && initialCurrency !== currency) {
      setCurrencyState(initialCurrency);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialCurrency]);

  const setCurrency = useCallback(
    (newCurrency: SupportedCurrencies) => {
      setCurrencyState(newCurrency);
      onCurrencyChange?.(newCurrency);
    },
    [onCurrencyChange],
  );

  const currencyInfo = SUPPORTED_CURRENCIES[currency] as {
    shortSymbol: string;
    longform: string;
  };

  return (
    <CurrencyContext.Provider
      value={{
        currency,
        currencyInfo,
        setCurrency,
      }}
    >
      {children}
    </CurrencyContext.Provider>
  );
};

export const useCurrency = (): CurrencyContextType => {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used inside CurrencyProvider');
  }
  return context;
};
